import { UNRESOLVED_OVERLAP_STATUSES, type Message, type Overlap, type Resolution } from "@botallion/contracts";
import {
  agentLabel,
  describeTerms,
  failedRounds,
  messageVerb,
  nameOf,
  nextMove,
  outcomeLabel,
  overlapKindLabel,
  overlapStatusLabel,
  viaLabel,
  withNames,
} from "../describe.ts";
import { html, type Html } from "../html.ts";
import type { DashboardState } from "../state.ts";
import { ago, chip, clock, empty, type Tone, type ViewContext } from "./common.ts";

type Entry =
  | { kind: "detected"; ts: string; overlap: Overlap }
  | { kind: "message"; ts: string; message: Message }
  | { kind: "resolution"; ts: string; resolution: Resolution };

/** Everything that happened on one overlap, in order. */
export function threadOf(state: DashboardState, o: Overlap): Entry[] {
  const entries: Entry[] = [{ kind: "detected", ts: o.createdAt, overlap: o }];
  for (const m of state.messages) if (m.overlapId === o.id) entries.push({ kind: "message", ts: m.ts, message: m });
  for (const r of state.resolutions) if (r.overlapId === o.id) entries.push({ kind: "resolution", ts: r.ts, resolution: r });
  // Stable: messages before the resolution they caused when both carry the same timestamp.
  return entries.map((e, i) => ({ e, i })).sort((a, b) => a.e.ts.localeCompare(b.e.ts) || a.i - b.i).map((x) => x.e);
}

/** When anything last happened on an overlap. */
export function lastActivity(state: DashboardState, o: Overlap): string {
  const t = threadOf(state, o).at(-1)?.ts ?? o.updatedAt;
  return t > o.updatedAt ? t : o.updatedAt;
}

const STATUS_TONE: Record<Overlap["status"], Tone> = { open: "block", negotiating: "warn", resolved: "good", escalated: "human", released: "muted" };

function entryView(state: DashboardState, o: Overlap, e: Entry): Html {
  switch (e.kind) {
    case "detected":
      return html`<li class="entry detected"><span class="t">${clock(e.ts)}</span>
        <div><strong>Detected</strong> ${o.detectedBy === "diff" ? "when a push collided" : `when ${nameOf(state, o.sessionB)} declared a claim`}.
        <span class="muted">${withNames(state, o.summary)}.</span></div></li>`;
    case "message": {
      const m = e.message;
      if (m.type === "info") {
        return html`<li class="entry info"><span class="t">${clock(e.ts)}</span><div><strong>${nameOf(state, m.from)}</strong> notes: ${withNames(state, m.body)}</div></li>`;
      }
      const side = m.from === o.sessionA ? "a" : "b";
      return html`<li class="entry msg side-${side} ${m.type}"><span class="t">${clock(e.ts)}</span>
        <div>
          <div class="who"><strong>${nameOf(state, m.from)}</strong> ${messageVerb(m)} <span class="muted">(to ${nameOf(state, m.to)})</span></div>
          ${m.proposal ? html`<div class="terms">${chip(outcomeLabel(m.proposal.outcome), "info")} ${describeTerms(state, m.proposal)}</div>` : ""}
          <blockquote>${withNames(state, m.body)}</blockquote>
        </div></li>`;
    }
    case "resolution": {
      const r = e.resolution;
      if (r.outcome === "escalated") {
        return html`<li class="entry escalated"><span class="t">${clock(e.ts)}</span><div>
          <strong>Escalated to a person.</strong> ${withNames(state, r.terms.summary)}.
          <ul class="positions">${r.terms.positions.map((p) => html`<li><strong>${nameOf(state, p.sessionId)}:</strong> ${withNames(state, p.position)}</li>`)}</ul>
          <div class="muted">Why: ${withNames(state, r.reason)}</div></div></li>`;
      }
      return html`<li class="entry settled ${r.via === "human" ? "human" : ""}"><span class="t">${clock(e.ts)}</span><div>
        <strong>Settled: ${outcomeLabel(r.outcome)}</strong>, ${viaLabel(state, r)}. ${describeTerms(state, r)}
        <div class="muted">Why: ${withNames(state, r.reason)}</div></div></li>`;
    }
  }
}

/** One overlap's card: what collided, who, every move, how it ended and whose move it is now. */
export function overlapCard(ctx: ViewContext, o: Overlap, open: boolean): Html {
  const { state, now } = ctx;
  const thread = threadOf(state, o);
  const move = nextMove(state, o, ctx.approver.canApprove);
  const rounds = failedRounds(state, o.id);
  const final = state.resolutions.filter((r) => r.overlapId === o.id && r.outcome !== "escalated").at(-1);
  const subjects = o.kind === "migration" ? "both add a migration" : o.subjects.join(", ");
  return html`<details class="thread ${o.status} ${o.severity}" data-overlap="${o.id}"${open ? " open" : ""}>
    <summary>
      <span class="sev ${o.severity}">${o.severity === "block" ? "BLOCK" : "WARN"}</span>
      <span class="what"><strong>${overlapKindLabel(o.kind)}</strong> <code>${subjects}</code></span>
      <span class="parties">${agentLabel(state, o.sessionA)} &#8644; ${agentLabel(state, o.sessionB)}</span>
      ${chip(overlapStatusLabel(o.status), STATUS_TONE[o.status])}
      ${final ? html`<span class="outcome">${describeTerms(state, final)}</span>` : ""}
    </summary>
    <ol class="entries">${thread.map((e) => entryView(state, o, e))}</ol>
    ${move ? html`<div class="next"><strong>Next:</strong> ${move} <span class="muted">Last move ${ago(now, lastActivity(state, o))}.${rounds ? ` Failed rounds: ${rounds} of 3.` : ""}</span></div>` : ""}
  </details>`;
}

/** Overlaps in the order a viewer wants them: unsettled first, then most recently active. Released ones last. */
export function orderedOverlaps(state: DashboardState): Overlap[] {
  const rank = (o: Overlap) => (UNRESOLVED_OVERLAP_STATUSES.includes(o.status) ? 0 : o.status === "resolved" ? 1 : 2);
  return Object.values(state.overlaps).sort((a, b) => rank(a) - rank(b) || lastActivity(state, b).localeCompare(lastActivity(state, a)));
}

/** Open by default: unsettled overlaps, and the one that changed last. The viewer's own choice wins. */
export function isOpen(ctx: ViewContext, o: Overlap, latestId: string | undefined): boolean {
  const choice = ctx.expanded[o.id];
  if (choice !== undefined) return choice;
  return UNRESOLVED_OVERLAP_STATUSES.includes(o.status) || o.id === latestId;
}

/** View 3: negotiation threads, messages and resolutions as they happen. */
export function negotiationsView(ctx: ViewContext, limit?: number): Html {
  const { state } = ctx;
  const list = orderedOverlaps(state);
  if (!list.length) return empty("No overlaps yet. When two agents claim the same ground, they settle it here. Nothing to do until then.");
  const latest = [...list].sort((a, b) => lastActivity(state, b).localeCompare(lastActivity(state, a)))[0]?.id;
  const shown = limit === undefined ? list : list.slice(0, limit);
  const unsettled = list.filter((o) => UNRESOLVED_OVERLAP_STATUSES.includes(o.status)).length;
  return html`<p class="summary-line">${list.length} overlap${list.length === 1 ? "" : "s"}: ${unsettled} unsettled, ${list.filter((o) => o.status === "resolved").length} settled.</p>
    ${shown.map((o) => overlapCard(ctx, o, isOpen(ctx, o, latest)))}
    ${limit !== undefined && list.length > limit ? html`<p class="muted">${list.length - limit} more on the Negotiations tab.</p>` : ""}`;
}
